/**
 * OneAmerica AssetCare Pricing Band Data
 * Per-field multiplier bands for non-linear premium scaling correction.
 * db = death benefit & monthly LTC, ltc = total LTC benefit.
 *
 * Single Pay / Single Life uses the packed demographic calibration datasets.
 * This file retains only Single Pay / Joint Life and 10-Pay fallback bands.
 *
 * Category 1 (AK, CT, etc.): joint and 10-Pay fallback bands
 * Category 2 (MT): age-aware bands in oneAmericaCat2DemoBands.ts (gender-independent, 4-part keys)
 * Category 3 (CA): joint and 10-Pay demographic-aware bands in oneAmericaCat3DemoBands.ts
 *
 * Base premium: $100k (Single Pay), $10k (10-Pay)
 *
 * Utility functions live in oneAmericaPricingUtils.ts.
 */

import type { ConfigBands } from "./oneAmericaPricingUtils";

const OA_SINGLE_PAY_JOINT_LIFE_BANDS: ConfigBands = {
  "None|None|2 Years": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 1, ltc: 1 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1.000072, ltc: 1.000072 },
    { premium: 150000, db: 1.00006, ltc: 1.00006 },
    { premium: 200000, db: 1.000045, ltc: 1.000045 },
    { premium: 300000, db: 1.07124, ltc: 1.07124 },
  ],
  "None|None|4 Years": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 1, ltc: 1 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1, ltc: 1 },
    { premium: 150000, db: 1, ltc: 1 },
    { premium: 200000, db: 1, ltc: 1 },
    { premium: 300000, db: 1.071864, ltc: 1.071864 },
  ],
  "None|None|6 Years": [
    { premium: 50000, db: 1.000121, ltc: 1.000121 },
    { premium: 75000, db: 1.00004, ltc: 1.00004 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 0.999976, ltc: 0.999976 },
    { premium: 150000, db: 1.00004, ltc: 1.00004 },
    { premium: 200000, db: 1, ltc: 1 },
    { premium: 300000, db: 1, ltc: 1 },
    { premium: 400000, db: 1.176956, ltc: 1.176956 },
  ],
  "None|None|Lifetime": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 1.000088, ltc: 1.000088 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1.000053, ltc: 1.000053 },
    { premium: 150000, db: 1, ltc: 1 },
    { premium: 200000, db: 1, ltc: 1 },
    { premium: 300000, db: 1, ltc: 1 },
    { premium: 400000, db: 1.156324, ltc: 1.156324 },
  ],
  "None|2%|2 Years": [
    { premium: 50000, db: 0.999901, ltc: 0.999901 },
    { premium: 75000, db: 0.999967, ltc: 0.999967 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1.00002, ltc: 1.00002 },
    { premium: 150000, db: 0.999967, ltc: 0.999967 },
    { premium: 200000, db: 1.065861, ltc: 1.065861 },
    { premium: 300000, db: 1.180069, ltc: 1.180069 },
  ],
  "None|2%|4 Years": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 0.999925, ltc: 0.999925 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 0.999955, ltc: 0.999955 },
    { premium: 150000, db: 0.999925, ltc: 0.999925 },
    { premium: 200000, db: 1.029622, ltc: 1.029622 },
    { premium: 300000, db: 1.218273, ltc: 1.218273 },
  ],
  "None|2%|6 Years": [
    { premium: 50000, db: 1.000142, ltc: 1.000142 },
    { premium: 75000, db: 1.000142, ltc: 1.000142 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1.000028, ltc: 1.000028 },
    { premium: 150000, db: 1.000047, ltc: 1.000047 },
    { premium: 200000, db: 1.000071, ltc: 1.000071 },
    { premium: 300000, db: 1.199753, ltc: 1.199753 },
    { premium: 400000, db: 1.407642, ltc: 1.407642 },
  ],
  "None|2%|Lifetime": [
    { premium: 50000, db: 1.000155, ltc: 1.000155 },
    { premium: 75000, db: 1.000052, ltc: 1.000052 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1.000093, ltc: 1.000093 },
    { premium: 150000, db: 1.000052, ltc: 1.000052 },
    { premium: 200000, db: 1.000077, ltc: 1.000077 },
    { premium: 300000, db: 1.147838, ltc: 1.147838 },
    { premium: 400000, db: 1.413877, ltc: 1.413877 },
  ],
  "None|3%|2 Years": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 1, ltc: 1 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1, ltc: 1 },
    { premium: 150000, db: 1.011307, ltc: 1.011307 },
    { premium: 200000, db: 1.132067, ltc: 1.132067 },
    { premium: 300000, db: 1.252862, ltc: 1.252862 },
  ],
  "None|3%|4 Years": [
    { premium: 50000, db: 1.00012, ltc: 1.00012 },
    { premium: 75000, db: 1.00012, ltc: 1.00012 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1.000024, ltc: 1.000024 },
    { premium: 150000, db: 1.00004, ltc: 1.00004 },
    { premium: 200000, db: 1.11341, ltc: 1.11341 },
    { premium: 300000, db: 1.314492, ltc: 1.314492 },
  ],
  "None|3%|6 Years": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 0.999895, ltc: 0.999895 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 0.999937, ltc: 0.999937 },
    { premium: 150000, db: 1, ltc: 1 },
    { premium: 200000, db: 1, ltc: 1 },
    { premium: 300000, db: 1.331184, ltc: 1.331184 },
    { premium: 400000, db: 1.559689, ltc: 1.559689 },
  ],
  "None|3%|Lifetime": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 1, ltc: 1 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 0.999863, ltc: 0.999863 },
    { premium: 150000, db: 0.999885, ltc: 0.999885 },
    { premium: 200000, db: 0.999914, ltc: 0.999914 },
    { premium: 300000, db: 1.287686, ltc: 1.287686 },
    { premium: 400000, db: 1.578952, ltc: 1.578952 },
  ],
  "None|5%|2 Years": [
    { premium: 50000, db: 1.00012, ltc: 1.00012 },
    { premium: 75000, db: 1.00004, ltc: 1.00004 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1.030049, ltc: 1.030049 },
    { premium: 150000, db: 1.143142, ltc: 1.143142 },
    { premium: 200000, db: 1.284628, ltc: 1.284628 },
    { premium: 300000, db: 1.426035, ltc: 1.426035 },
  ],
  "None|5%|4 Years": [
    { premium: 50000, db: 1.000141, ltc: 1.000141 },
    { premium: 75000, db: 0.999953, ltc: 0.999953 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1.000028, ltc: 1.000028 },
    { premium: 150000, db: 1.061123, ltc: 1.061123 },
    { premium: 200000, db: 1.298802, ltc: 1.298802 },
    { premium: 300000, db: 1.536481, ltc: 1.536481 },
  ],
  "None|5%|6 Years": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 1, ltc: 1 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1, ltc: 1 },
    { premium: 150000, db: 1, ltc: 1 },
    { premium: 200000, db: 1.070702, ltc: 1.070702 },
    { premium: 300000, db: 1.626695, ltc: 1.626695 },
    { premium: 400000, db: 1.904739, ltc: 1.904739 },
  ],
  "None|5%|Lifetime": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 1, ltc: 1 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1.000168, ltc: 1.000168 },
    { premium: 150000, db: 1.00014, ltc: 1.00014 },
    { premium: 200000, db: 1.000105, ltc: 1.000105 },
    { premium: 300000, db: 1.600112, ltc: 1.600112 },
    { premium: 400000, db: 1.948511, ltc: 1.948511 },
  ],
  "2%|2%|2 Years": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 0.99992, ltc: 0.99992 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 0.999952, ltc: 0.999952 },
    { premium: 150000, db: 0.99992, ltc: 0.99992 },
    { premium: 200000, db: 0.99994, ltc: 0.99994 },
  ],
  "2%|2%|4 Years": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 0.999911, ltc: 0.999911 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 0.999947, ltc: 0.999947 },
    { premium: 150000, db: 0.999911, ltc: 0.999911 },
    { premium: 200000, db: 0.999933, ltc: 0.999933 },
  ],
  "2%|2%|6 Years": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 1, ltc: 1 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1, ltc: 1 },
    { premium: 150000, db: 1.000109, ltc: 1.000109 },
    { premium: 200000, db: 1.000082, ltc: 1.000082 },
  ],
  "2%|2%|Lifetime": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 1, ltc: 1 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1, ltc: 1 },
    { premium: 150000, db: 1, ltc: 1 },
    { premium: 200000, db: 0.999912, ltc: 0.999912 },
    { premium: 300000, db: 0.999941, ltc: 0.999941 },
  ],
  "3%|3%|2 Years": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 1, ltc: 1 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1, ltc: 1 },
    { premium: 150000, db: 1, ltc: 1 },
  ],
  "3%|3%|4 Years": [
    { premium: 50000, db: 1.000156, ltc: 1.000156 },
    { premium: 75000, db: 0.999948, ltc: 0.999948 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1.000031, ltc: 1.000031 },
    { premium: 150000, db: 1.000052, ltc: 1.000052 },
    { premium: 200000, db: 1, ltc: 1 },
  ],
  "3%|3%|6 Years": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 1, ltc: 1 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1, ltc: 1 },
    { premium: 150000, db: 1, ltc: 1 },
    { premium: 200000, db: 1, ltc: 1 },
  ],
  "3%|3%|Lifetime": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 0.999862, ltc: 0.999862 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 0.999917, ltc: 0.999917 },
    { premium: 150000, db: 0.999862, ltc: 0.999862 },
    { premium: 200000, db: 0.999896, ltc: 0.999896 },
  ],
  "5%|5%|2 Years": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 0.999872, ltc: 0.999872 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 0.999923, ltc: 0.999923 },
    { premium: 150000, db: 1, ltc: 1 },
  ],
  "5%|5%|4 Years": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 1.000142, ltc: 1.000142 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1.000085, ltc: 1.000085 },
    { premium: 150000, db: 1, ltc: 1 },
    { premium: 200000, db: 1, ltc: 1 },
  ],
  "5%|5%|6 Years": [
    { premium: 50000, db: 1.100026, ltc: 1.100026 },
    { premium: 75000, db: 0.999736, ltc: 0.999736 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 0.999947, ltc: 0.999947 },
    { premium: 150000, db: 0.999912, ltc: 0.999912 },
    { premium: 200000, db: 0.999868, ltc: 0.999868 },
  ],
  "5%|5%|Lifetime": [
    { premium: 50000, db: 1.180051, ltc: 1.180051 },
    { premium: 75000, db: 0.999718, ltc: 0.999718 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 0.999944, ltc: 0.999944 },
    { premium: 150000, db: 0.999906, ltc: 0.999906 },
    { premium: 200000, db: 0.999859, ltc: 0.999859 },
  ],
};

const OA_10PAY_SINGLE_LIFE_BANDS: ConfigBands = {
  "None|None|2 Years": [
    { premium: 5000, db: 0.979989, ltc: 0.979989 },
    { premium: 7500, db: 0.999941, ltc: 0.999941 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 0.999941, ltc: 0.999941 },
    { premium: 15500, db: 0.999903, ltc: 0.999903 },
    { premium: 20000, db: 0.999911, ltc: 0.999911 },
    { premium: 25000, db: 0.999965, ltc: 0.999965 },
    { premium: 30000, db: 0.999941, ltc: 0.999941 },
  ],
  "None|None|4 Years": [
    { premium: 5000, db: 0.979978, ltc: 0.979978 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1, ltc: 1 },
    { premium: 15500, db: 1.000035, ltc: 1.000035 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 1, ltc: 1 },
    { premium: 30000, db: 1, ltc: 1 },
  ],
  "None|None|6 Years": [
    { premium: 5000, db: 0.980182, ltc: 0.980182 },
    { premium: 7500, db: 0.986656, ltc: 0.986656 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1, ltc: 1 },
    { premium: 15500, db: 1.000089, ltc: 1.000089 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 1.000079, ltc: 1.000079 },
    { premium: 30000, db: 1.000066, ltc: 1.000066 },
  ],
  "None|None|Lifetime": [
    { premium: 5000, db: 0.980036, ltc: 0.980036 },
    { premium: 7500, db: 0.986758, ltc: 0.986758 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000067, ltc: 1.000067 },
    { premium: 15500, db: 1.000072, ltc: 1.000072 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 1.00004, ltc: 1.00004 },
    { premium: 30000, db: 1, ltc: 1 },
  ],
  "None|2%|2 Years": [
    { premium: 5000, db: 0.980128, ltc: 0.980128 },
    { premium: 7500, db: 0.986691, ltc: 0.986691 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000061, ltc: 1.000061 },
    { premium: 15500, db: 1.000029, ltc: 1.000029 },
    { premium: 20000, db: 1.000091, ltc: 1.000091 },
    { premium: 25000, db: 1.000036, ltc: 1.000036 },
    { premium: 30000, db: 1.000061, ltc: 1.000061 },
  ],
  "None|2%|4 Years": [
    { premium: 5000, db: 0.979977, ltc: 0.979977 },
    { premium: 7500, db: 0.986777, ltc: 0.986777 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000126, ltc: 1.000126 },
    { premium: 15500, db: 1.000037, ltc: 1.000037 },
    { premium: 20000, db: 1.000094, ltc: 1.000094 },
    { premium: 25000, db: 1.000076, ltc: 1.000076 },
    { premium: 30000, db: 1.000063, ltc: 1.000063 },
  ],
  "None|2%|6 Years": [
    { premium: 5000, db: 0.980004, ltc: 0.980004 },
    { premium: 7500, db: 0.98674, ltc: 0.98674 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.00007, ltc: 1.00007 },
    { premium: 15500, db: 0.999993, ltc: 0.999993 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 1.000042, ltc: 1.000042 },
    { premium: 30000, db: 1, ltc: 1 },
  ],
  "None|2%|Lifetime": [
    { premium: 5000, db: 0.980039, ltc: 0.980039 },
    { premium: 7500, db: 0.986478, ltc: 0.986478 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 0.999928, ltc: 0.999928 },
    { premium: 15500, db: 0.999938, ltc: 0.999938 },
    { premium: 20000, db: 0.999893, ltc: 0.999893 },
    { premium: 25000, db: 0.999957, ltc: 0.999957 },
    { premium: 30000, db: 0.999928, ltc: 0.999928 },
  ],
  "None|3%|2 Years": [
    { premium: 5000, db: 0.979963, ltc: 0.979963 },
    { premium: 7500, db: 0.986518, ltc: 0.986518 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 0.999876, ltc: 0.999876 },
    { premium: 15500, db: 0.99994, ltc: 0.99994 },
    { premium: 20000, db: 0.999907, ltc: 0.999907 },
    { premium: 25000, db: 0.999926, ltc: 0.999926 },
    { premium: 30000, db: 1.017563, ltc: 1.017563 },
  ],
  "None|3%|4 Years": [
    { premium: 5000, db: 0.979853, ltc: 0.979853 },
    { premium: 7500, db: 0.986698, ltc: 0.986698 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1, ltc: 1 },
    { premium: 15500, db: 0.999988, ltc: 0.999988 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 1, ltc: 1 },
    { premium: 30000, db: 1.017371, ltc: 1.017371 },
  ],
  "None|3%|6 Years": [
    { premium: 5000, db: 0.980101, ltc: 0.980101 },
    { premium: 7500, db: 0.986661, ltc: 0.986661 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 0.999927, ltc: 0.999927 },
    { premium: 15500, db: 0.999979, ltc: 0.999979 },
    { premium: 20000, db: 0.999891, ltc: 0.999891 },
    { premium: 25000, db: 0.999956, ltc: 0.999956 },
    { premium: 30000, db: 0.999927, ltc: 0.999927 },
  ],
  "None|3%|Lifetime": [
    { premium: 5000, db: 0.980112, ltc: 0.980112 },
    { premium: 7500, db: 0.986518, ltc: 0.986518 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 0.999926, ltc: 0.999926 },
    { premium: 15500, db: 0.999964, ltc: 0.999964 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 0.999955, ltc: 0.999955 },
    { premium: 30000, db: 1, ltc: 1 },
  ],
  "None|5%|2 Years": [
    { premium: 5000, db: 0.979829, ltc: 0.979829 },
    { premium: 7500, db: 0.986811, ltc: 0.986811 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1, ltc: 1 },
    { premium: 15500, db: 1.000025, ltc: 1.000025 },
    { premium: 20000, db: 1.005528, ltc: 1.005528 },
    { premium: 25000, db: 1.04104, ltc: 1.04104 },
    { premium: 30000, db: 1.06465, ltc: 1.06465 },
  ],
  "None|5%|4 Years": [
    { premium: 5000, db: 0.979825, ltc: 0.979825 },
    { premium: 7500, db: 0.986618, ltc: 0.986618 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 0.999932, ltc: 0.999932 },
    { premium: 15500, db: 0.999888, ltc: 0.999888 },
    { premium: 20000, db: 0.999898, ltc: 0.999898 },
    { premium: 25000, db: 1.044793, ltc: 1.044793 },
    { premium: 30000, db: 1.077848, ltc: 1.077848 },
  ],
  "None|5%|6 Years": [
    { premium: 5000, db: 0.99073, ltc: 0.99073 },
    { premium: 7500, db: 0.98661, ltc: 0.98661 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000079, ltc: 1.000079 },
    { premium: 15500, db: 1.000023, ltc: 1.000023 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 1.022106, ltc: 1.022106 },
    { premium: 30000, db: 1.093416, ltc: 1.093416 },
  ],
  "None|5%|Lifetime": [
    { premium: 5000, db: 1.020318, ltc: 1.020318 },
    { premium: 7500, db: 0.996491, ltc: 0.996491 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010037, ltc: 1.010037 },
    { premium: 15500, db: 1.009989, ltc: 1.009989 },
    { premium: 20000, db: 1.010037, ltc: 1.010037 },
    { premium: 25000, db: 1.024725, ltc: 1.024725 },
    { premium: 30000, db: 1.102734, ltc: 1.102734 },
  ],
  "2%|2%|2 Years": [
    { premium: 5000, db: 0.980266, ltc: 0.980266 },
    { premium: 7500, db: 0.98671, ltc: 0.98671 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1, ltc: 1 },
    { premium: 15500, db: 1.000091, ltc: 1.000091 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 1.000081, ltc: 1.000081 },
    { premium: 30000, db: 1.000067, ltc: 1.000067 },
  ],
  "2%|2%|4 Years": [
    { premium: 5000, db: 0.979829, ltc: 0.979829 },
    { premium: 7500, db: 0.986761, ltc: 0.986761 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000069, ltc: 1.000069 },
    { premium: 15500, db: 1.000007, ltc: 1.000007 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 1.000042, ltc: 1.000042 },
    { premium: 30000, db: 1.000069, ltc: 1.000069 },
  ],
  "2%|2%|6 Years": [
    { premium: 5000, db: 0.980032, ltc: 0.980032 },
    { premium: 7500, db: 0.986612, ltc: 0.986612 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 0.999923, ltc: 0.999923 },
    { premium: 15500, db: 0.999948, ltc: 0.999948 },
    { premium: 20000, db: 0.999885, ltc: 0.999885 },
    { premium: 25000, db: 0.999954, ltc: 0.999954 },
    { premium: 30000, db: 0.999923, ltc: 0.999923 },
  ],
  "2%|2%|Lifetime": [
    { premium: 5000, db: 0.980136, ltc: 0.980136 },
    { premium: 7500, db: 0.986523, ltc: 0.986523 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 0.999922, ltc: 0.999922 },
    { premium: 15500, db: 0.999932, ltc: 0.999932 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 0.999953, ltc: 0.999953 },
    { premium: 30000, db: 0.999922, ltc: 0.999922 },
  ],
  "3%|3%|2 Years": [
    { premium: 5000, db: 0.980282, ltc: 0.980282 },
    { premium: 7500, db: 0.986638, ltc: 0.986638 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000072, ltc: 1.000072 },
    { premium: 15500, db: 1.000105, ltc: 1.000105 },
    { premium: 20000, db: 1.000108, ltc: 1.000108 },
    { premium: 25000, db: 1.000043, ltc: 1.000043 },
    { premium: 30000, db: 1.000072, ltc: 1.000072 },
  ],
  "3%|3%|4 Years": [
    { premium: 5000, db: 0.979987, ltc: 0.979987 },
    { premium: 7500, db: 0.986733, ltc: 0.986733 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000075, ltc: 1.000075 },
    { premium: 15500, db: 1.000022, ltc: 1.000022 },
    { premium: 20000, db: 1.000112, ltc: 1.000112 },
    { premium: 25000, db: 1.000045, ltc: 1.000045 },
    { premium: 30000, db: 1.000075, ltc: 1.000075 },
  ],
  "3%|3%|6 Years": [
    { premium: 5000, db: 1.051728, ltc: 1.051728 },
    { premium: 7500, db: 0.996551, ltc: 0.996551 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010009, ltc: 1.010009 },
    { premium: 15500, db: 1.01015, ltc: 1.01015 },
    { premium: 20000, db: 1.010093, ltc: 1.010093 },
    { premium: 25000, db: 1.010043, ltc: 1.010043 },
    { premium: 30000, db: 1.010093, ltc: 1.010093 },
  ],
  "3%|3%|Lifetime": [
    { premium: 5000, db: 1.071741, ltc: 1.071741 },
    { premium: 7500, db: 0.996657, ltc: 0.996657 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010028, ltc: 1.010028 },
    { premium: 15500, db: 1.009962, ltc: 1.009962 },
    { premium: 20000, db: 1.010028, ltc: 1.010028 },
    { premium: 25000, db: 1.010028, ltc: 1.010028 },
    { premium: 30000, db: 1.010028, ltc: 1.010028 },
  ],
  "5%|5%|2 Years": [
    { premium: 5000, db: 1.073397, ltc: 1.073397 },
    { premium: 7500, db: 0.99648, ltc: 0.99648 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010044, ltc: 1.010044 },
    { premium: 15500, db: 1.010027, ltc: 1.010027 },
    { premium: 20000, db: 1.010044, ltc: 1.010044 },
    { premium: 25000, db: 1.010044, ltc: 1.010044 },
  ],
  "5%|5%|4 Years": [
    { premium: 5000, db: 1.115035, ltc: 1.115035 },
    { premium: 7500, db: 0.996611, ltc: 0.996611 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010166, ltc: 1.010166 },
    { premium: 15500, db: 1.0102, ltc: 1.0102 },
    { premium: 20000, db: 1.010166, ltc: 1.010166 },
    { premium: 25000, db: 1.010166, ltc: 1.010166 },
  ],
  "5%|5%|6 Years": [
    { premium: 5000, db: 1.257695, ltc: 1.257695 },
    { premium: 7500, db: 0.99658, ltc: 0.99658 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010058, ltc: 1.010058 },
    { premium: 15500, db: 1.010182, ltc: 1.010182 },
    { premium: 20000, db: 1.010109, ltc: 1.010109 },
    { premium: 25000, db: 1.010139, ltc: 1.010139 },
  ],
  "5%|5%|Lifetime": [
    { premium: 5000, db: 1.277352, ltc: 1.277352 },
    { premium: 7500, db: 0.996629, ltc: 0.996629 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010113, ltc: 1.010113 },
    { premium: 15500, db: 1.010153, ltc: 1.010153 },
    { premium: 20000, db: 1.010113, ltc: 1.010113 },
    { premium: 25000, db: 1.010113, ltc: 1.010113 },
    { premium: 30000, db: 1.010113, ltc: 1.010113 },
  ],
};

const OA_10PAY_JOINT_LIFE_BANDS: ConfigBands = {
  "None|None|2 Years": [
    { premium: 5000, db: 0.980142, ltc: 0.980142 },
    { premium: 7500, db: 1.000046, ltc: 1.000046 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000046, ltc: 1.000046 },
    { premium: 15500, db: 1.00004, ltc: 1.00004 },
    { premium: 20000, db: 1.000069, ltc: 1.000069 },
    { premium: 25000, db: 1.000028, ltc: 1.000028 },
    { premium: 30000, db: 1.000046, ltc: 1.000046 },
  ],
  "None|None|4 Years": [
    { premium: 5000, db: 0.980084, ltc: 0.980084 },
    { premium: 7500, db: 1, ltc: 1 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000101, ltc: 1.000101 },
    { premium: 15500, db: 1.000058, ltc: 1.000058 },
    { premium: 20000, db: 1.000075, ltc: 1.000075 },
    { premium: 25000, db: 1.00006, ltc: 1.00006 },
    { premium: 30000, db: 1.00005, ltc: 1.00005 },
  ],
  "None|None|6 Years": [
    { premium: 5000, db: 0.979825, ltc: 0.979825 },
    { premium: 7500, db: 1.00006, ltc: 1.00006 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.00006, ltc: 1.00006 },
    { premium: 15500, db: 1.000052, ltc: 1.000052 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 1.000036, ltc: 1.000036 },
    { premium: 30000, db: 1, ltc: 1 },
  ],
  "None|None|Lifetime": [
    { premium: 5000, db: 0.979819, ltc: 0.979819 },
    { premium: 7500, db: 0.98661, ltc: 0.98661 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000064, ltc: 1.000064 },
    { premium: 15500, db: 1.000043, ltc: 1.000043 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 1.000038, ltc: 1.000038 },
    { premium: 30000, db: 1, ltc: 1 },
  ],
  "None|2%|2 Years": [
    { premium: 5000, db: 0.979952, ltc: 0.979952 },
    { premium: 7500, db: 1, ltc: 1 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1, ltc: 1 },
    { premium: 15500, db: 0.999981, ltc: 0.999981 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 0.99994, ltc: 0.99994 },
    { premium: 30000, db: 1.046828, ltc: 1.046828 },
  ],
  "None|2%|4 Years": [
    { premium: 5000, db: 0.979917, ltc: 0.979917 },
    { premium: 7500, db: 1.000055, ltc: 1.000055 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 0.999945, ltc: 0.999945 },
    { premium: 15500, db: 0.99992, ltc: 0.99992 },
    { premium: 20000, db: 0.999917, ltc: 0.999917 },
    { premium: 25000, db: 0.999967, ltc: 0.999967 },
    { premium: 30000, db: 1.026777, ltc: 1.026777 },
  ],
  "None|2%|6 Years": [
    { premium: 5000, db: 0.979885, ltc: 0.979885 },
    { premium: 7500, db: 0.98659, ltc: 0.98659 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1, ltc: 1 },
    { premium: 15500, db: 1.000053, ltc: 1.000053 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 1, ltc: 1 },
    { premium: 30000, db: 1, ltc: 1 },
  ],
  "None|2%|Lifetime": [
    { premium: 5000, db: 0.979764, ltc: 0.979764 },
    { premium: 7500, db: 0.986732, ltc: 0.986732 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 0.999926, ltc: 0.999926 },
    { premium: 15500, db: 0.99995, ltc: 0.99995 },
    { premium: 20000, db: 0.999889, ltc: 0.999889 },
    { premium: 25000, db: 0.999956, ltc: 0.999956 },
    { premium: 30000, db: 0.999926, ltc: 0.999926 },
  ],
  "None|3%|2 Years": [
    { premium: 5000, db: 0.979899, ltc: 0.979899 },
    { premium: 7500, db: 1, ltc: 1 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1, ltc: 1 },
    { premium: 15500, db: 1.000061, ltc: 1.000061 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 1.044975, ltc: 1.044975 },
    { premium: 30000, db: 1.099665, ltc: 1.099665 },
  ],
  "None|3%|4 Years": [
    { premium: 5000, db: 0.979859, ltc: 0.979859 },
    { premium: 7500, db: 1, ltc: 1 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1, ltc: 1 },
    { premium: 15500, db: 1, ltc: 1 },
    { premium: 20000, db: 0.999912, ltc: 0.999912 },
    { premium: 25000, db: 1.005724, ltc: 1.005724 },
    { premium: 30000, db: 1.095524, ltc: 1.095524 },
  ],
  "None|3%|6 Years": [
    { premium: 5000, db: 0.979875, ltc: 0.979875 },
    { premium: 7500, db: 0.986583, ltc: 0.986583 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1, ltc: 1 },
    { premium: 15500, db: 1.000058, ltc: 1.000058 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 1, ltc: 1 },
    { premium: 30000, db: 1, ltc: 1 },
  ],
  "None|3%|Lifetime": [
    { premium: 5000, db: 1.026854, ltc: 1.026854 },
    { premium: 7500, db: 0.996633, ltc: 0.996633 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010101, ltc: 1.010101 },
    { premium: 15500, db: 1.010101, ltc: 1.010101 },
    { premium: 20000, db: 1.010101, ltc: 1.010101 },
    { premium: 25000, db: 1.010101, ltc: 1.010101 },
    { premium: 30000, db: 1.010101, ltc: 1.010101 },
  ],
  "None|5%|2 Years": [
    { premium: 5000, db: 0.979807, ltc: 0.979807 },
    { premium: 7500, db: 0.999941, ltc: 0.999941 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000059, ltc: 1.000059 },
    { premium: 15500, db: 1.000085, ltc: 1.000085 },
    { premium: 20000, db: 1.065408, ltc: 1.065408 },
    { premium: 25000, db: 1.159333, ltc: 1.159333 },
    { premium: 30000, db: 1.221949, ltc: 1.221949 },
  ],
  "None|5%|4 Years": [
    { premium: 5000, db: 0.979716, ltc: 0.979716 },
    { premium: 7500, db: 0.986613, ltc: 0.986613 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1, ltc: 1 },
    { premium: 15500, db: 0.999935, ltc: 0.999935 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 1.145801, ltc: 1.145801 },
    { premium: 30000, db: 1.250439, ltc: 1.250439 },
  ],
  "None|5%|6 Years": [
    { premium: 5000, db: 1.131686, ltc: 1.131686 },
    { premium: 7500, db: 0.996651, ltc: 0.996651 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010046, ltc: 1.010046 },
    { premium: 15500, db: 1.010046, ltc: 1.010046 },
    { premium: 20000, db: 1.010046, ltc: 1.010046 },
    { premium: 25000, db: 1.010046, ltc: 1.010046 },
    { premium: 30000, db: 1.151778, ltc: 1.151778 },
  ],
  "None|5%|Lifetime": [
    { premium: 5000, db: 1.236428, ltc: 1.236428 },
    { premium: 7500, db: 0.996737, ltc: 0.996737 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010185, ltc: 1.010185 },
    { premium: 15500, db: 1.010325, ltc: 1.010325 },
    { premium: 20000, db: 1.010234, ltc: 1.010234 },
    { premium: 25000, db: 1.010264, ltc: 1.010264 },
    { premium: 30000, db: 1.043805, ltc: 1.043805 },
  ],
  "2%|2%|2 Years": [
    { premium: 5000, db: 0.979786, ltc: 0.979786 },
    { premium: 7500, db: 1.000059, ltc: 1.000059 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000059, ltc: 1.000059 },
    { premium: 15500, db: 1.000006, ltc: 1.000006 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 1.000035, ltc: 1.000035 },
  ],
  "2%|2%|4 Years": [
    { premium: 5000, db: 0.980015, ltc: 0.980015 },
    { premium: 7500, db: 0.986677, ltc: 0.986677 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000128, ltc: 1.000128 },
    { premium: 15500, db: 1.000099, ltc: 1.000099 },
    { premium: 20000, db: 1.000096, ltc: 1.000096 },
    { premium: 25000, db: 1.000077, ltc: 1.000077 },
    { premium: 30000, db: 1.000064, ltc: 1.000064 },
  ],
  "2%|2%|6 Years": [
    { premium: 5000, db: 0.979866, ltc: 0.979866 },
    { premium: 7500, db: 0.986809, ltc: 0.986809 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000077, ltc: 1.000077 },
    { premium: 15500, db: 1.000067, ltc: 1.000067 },
    { premium: 20000, db: 1.000116, ltc: 1.000116 },
    { premium: 25000, db: 1.000046, ltc: 1.000046 },
    { premium: 30000, db: 1.000077, ltc: 1.000077 },
  ],
  "2%|2%|Lifetime": [
    { premium: 5000, db: 1.046447, ltc: 1.046447 },
    { premium: 7500, db: 0.996569, ltc: 0.996569 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010126, ltc: 1.010126 },
    { premium: 15500, db: 1.010099, ltc: 1.010099 },
    { premium: 20000, db: 1.010168, ltc: 1.010168 },
    { premium: 25000, db: 1.010193, ltc: 1.010193 },
    { premium: 30000, db: 1.010126, ltc: 1.010126 },
  ],
  "3%|3%|2 Years": [
    { premium: 5000, db: 0.979976, ltc: 0.979976 },
    { premium: 7500, db: 0.986784, ltc: 0.986784 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1, ltc: 1 },
    { premium: 15500, db: 1.000039, ltc: 1.000039 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 1.00008, ltc: 1.00008 },
  ],
  "3%|3%|4 Years": [
    { premium: 5000, db: 0.979771, ltc: 0.979771 },
    { premium: 7500, db: 0.986514, ltc: 0.986514 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1, ltc: 1 },
    { premium: 15500, db: 0.999943, ltc: 0.999943 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 0.999912, ltc: 0.999912 },
    { premium: 30000, db: 0.999927, ltc: 0.999927 },
  ],
  "3%|3%|6 Years": [
    { premium: 5000, db: 1.12345, ltc: 1.12345 },
    { premium: 7500, db: 0.996945, ltc: 0.996945 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010243, ltc: 1.010243 },
    { premium: 15500, db: 1.010173, ltc: 1.010173 },
    { premium: 20000, db: 1.010243, ltc: 1.010243 },
    { premium: 25000, db: 1.010243, ltc: 1.010243 },
    { premium: 30000, db: 1.010243, ltc: 1.010243 },
  ],
  "3%|3%|Lifetime": [
    { premium: 5000, db: 1.208817, ltc: 1.208817 },
    { premium: 7500, db: 0.99652, ltc: 0.99652 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010054, ltc: 1.010054 },
    { premium: 15500, db: 1.010216, ltc: 1.010216 },
    { premium: 20000, db: 1.010151, ltc: 1.010151 },
    { premium: 25000, db: 1.010093, ltc: 1.010093 },
    { premium: 30000, db: 1.010151, ltc: 1.010151 },
  ],
  "5%|5%|2 Years": [
    { premium: 5000, db: 1.10557, ltc: 1.10557 },
    { premium: 7500, db: 0.99664, ltc: 0.99664 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010256, ltc: 1.010256 },
    { premium: 15500, db: 1.010182, ltc: 1.010182 },
    { premium: 20000, db: 1.010212, ltc: 1.010212 },
    { premium: 25000, db: 1.010292, ltc: 1.010292 },
  ],
  "5%|5%|4 Years": [
    { premium: 5000, db: 1.220141, ltc: 1.220141 },
    { premium: 7500, db: 0.996487, ltc: 0.996487 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010148, ltc: 1.010148 },
    { premium: 15500, db: 1.010048, ltc: 1.010048 },
    { premium: 20000, db: 1.0101, ltc: 1.0101 },
    { premium: 25000, db: 1.01007, ltc: 1.01007 },
  ],
  "5%|5%|6 Years": [
    { premium: 5000, db: 1.498203, ltc: 1.498203 },
    { premium: 7500, db: 0.998802, ltc: 0.998802 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010304, ltc: 1.010304 },
    { premium: 15500, db: 1.010181, ltc: 1.010181 },
    { premium: 20000, db: 1.010244, ltc: 1.010244 },
    { premium: 25000, db: 1.010208, ltc: 1.010208 },
    { premium: 30000, db: 1.010304, ltc: 1.010304 },
  ],
  "5%|5%|Lifetime": [
    { premium: 5000, db: 1.602461, ltc: 1.602461 },
    { premium: 7500, db: 1.068307, ltc: 1.068307 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.00346, ltc: 1.00346 },
    { premium: 15500, db: 1.003584, ltc: 1.003584 },
    { premium: 20000, db: 1.010188, ltc: 1.010188 },
    { premium: 25000, db: 1.010073, ltc: 1.010073 },
    { premium: 30000, db: 1.010124, ltc: 1.010124 },
  ],
};


const OA_CAT1_PRODUCT_BANDS: Record<string, ConfigBands> = {
  sp_joint: OA_SINGLE_PAY_JOINT_LIFE_BANDS,
  "10pay_single": OA_10PAY_SINGLE_LIFE_BANDS,
  "10pay_joint": OA_10PAY_JOINT_LIFE_BANDS,
};

const OA_CAT3_SINGLE_PAY_JOINT_LIFE_BANDS: ConfigBands = {
  "None|None|2 Years": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 1.000003, ltc: 1.000003 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1.000002, ltc: 1.000002 },
    { premium: 150000, db: 1.000003, ltc: 1.000003 },
    { premium: 200000, db: 1.000002, ltc: 1.000002 },
    { premium: 300000, db: 1.0712, ltc: 0.910691 },
  ],
  "None|None|4 Years": [
    { premium: 50000, db: 0.999996, ltc: 0.999996 },
    { premium: 75000, db: 0.999999, ltc: 0.999999 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1.000001, ltc: 1.000001 },
    { premium: 150000, db: 1.000001, ltc: 1.000001 },
    { premium: 200000, db: 1.000002, ltc: 1.000002 },
    { premium: 300000, db: 1.071865, ltc: 0.909743 },
  ],
  "None|None|6 Years": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 1.000003, ltc: 1.000003 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1.000002, ltc: 1.000002 },
    { premium: 150000, db: 1, ltc: 1 },
    { premium: 200000, db: 1.000003, ltc: 1.000003 },
    { premium: 300000, db: 1.000002, ltc: 1.000002 },
    { premium: 400000, db: 1.176946, ltc: 0.862757 },
  ],
  "None|None|Lifetime": [
    { premium: 50000, db: 0.999995, ltc: 1 },
    { premium: 75000, db: 0.999995, ltc: 1 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 0.999999, ltc: 1 },
    { premium: 150000, db: 0.999998, ltc: 1 },
    { premium: 200000, db: 1, ltc: 1 },
    { premium: 300000, db: 1, ltc: 1 },
    { premium: 400000, db: 1.156303, ltc: 1 },
  ],
  "None|3%|2 Years": [
    { premium: 50000, db: 0.999996, ltc: 0.999996 },
    { premium: 75000, db: 0.999999, ltc: 0.999997 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1.000001, ltc: 1 },
    { premium: 150000, db: 1.011317, ltc: 0.993581 },
    { premium: 200000, db: 1.132082, ltc: 0.925074 },
    { premium: 300000, db: 1.252851, ltc: 0.856568 },
  ],
  "None|3%|4 Years": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 1, ltc: 1 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1.000004, ltc: 1.000004 },
    { premium: 150000, db: 1.000003, ltc: 1.000003 },
    { premium: 200000, db: 1.113362, ltc: 0.926168 },
    { premium: 300000, db: 1.314449, ltc: 0.795193 },
  ],
  "None|3%|6 Years": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 0.999996, ltc: 0.999995 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 0.999997, ltc: 0.999998 },
    { premium: 150000, db: 1, ltc: 1 },
    { premium: 200000, db: 1, ltc: 1 },
    { premium: 300000, db: 1.331212, ltc: 0.868769 },
    { premium: 400000, db: 1.559689, ltc: 0.778243 },
  ],
  "None|3%|Lifetime": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 1, ltc: 1 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1, ltc: 1 },
    { premium: 150000, db: 1.000005, ltc: 1 },
    { premium: 200000, db: 1.000004, ltc: 1 },
    { premium: 300000, db: 1.287813, ltc: 1 },
    { premium: 400000, db: 1.57907, ltc: 1 },
  ],
  "None|5%|2 Years": [
    { premium: 50000, db: 0.999995, ltc: 0.999995 },
    { premium: 75000, db: 0.999995, ltc: 0.999995 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1.029983, ltc: 0.991633 },
    { premium: 150000, db: 1.143121, ltc: 0.960066 },
    { premium: 200000, db: 1.284543, ltc: 0.920608 },
    { premium: 300000, db: 1.425964, ltc: 0.881149 },
  ],
  "None|5%|4 Years": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 0.999996, ltc: 0.999997 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 0.999998, ltc: 0.999998 },
    { premium: 150000, db: 1.061099, ltc: 0.975602 },
    { premium: 200000, db: 1.29879, ltc: 0.880686 },
    { premium: 300000, db: 1.536479, ltc: 0.785769 },
  ],
  "None|5%|6 Years": [
    { premium: 50000, db: 0.999992, ltc: 0.999993 },
    { premium: 75000, db: 0.999997, ltc: 0.999997 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1.000002, ltc: 1.000001 },
    { premium: 150000, db: 1.000003, ltc: 1.000002 },
    { premium: 200000, db: 1.070723, ltc: 0.982783 },
    { premium: 300000, db: 1.626734, ltc: 0.8474 },
    { premium: 400000, db: 1.90474, ltc: 0.779708 },
  ],
  "None|5%|Lifetime": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 1.000006, ltc: 1 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1.000003, ltc: 1 },
    { premium: 150000, db: 1, ltc: 1 },
    { premium: 200000, db: 1.000004, ltc: 1 },
    { premium: 300000, db: 1.599969, ltc: 1 },
    { premium: 400000, db: 1.948354, ltc: 1 },
  ],
  "3%|3%|2 Years": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 1, ltc: 1 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1, ltc: 1.000002 },
    { premium: 150000, db: 1, ltc: 1.000002 },
  ],
  "3%|3%|4 Years": [
    { premium: 50000, db: 0.999993, ltc: 0.999992 },
    { premium: 75000, db: 0.999998, ltc: 0.999996 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1.000001, ltc: 1.000001 },
    { premium: 150000, db: 1.000002, ltc: 1.000001 },
    { premium: 200000, db: 1.000003, ltc: 1.000002 },
  ],
  "3%|3%|6 Years": [
    { premium: 50000, db: 0.999992, ltc: 0.999991 },
    { premium: 75000, db: 0.999992, ltc: 0.999992 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 0.999998, ltc: 0.999999 },
    { premium: 150000, db: 0.999997, ltc: 0.999998 },
    { premium: 200000, db: 1, ltc: 1 },
  ],
  "3%|3%|Lifetime": [
    { premium: 50000, db: 1, ltc: 1 },
    { premium: 75000, db: 1, ltc: 1 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1, ltc: 1 },
    { premium: 150000, db: 1, ltc: 1 },
    { premium: 200000, db: 1, ltc: 1 },
  ],
  "5%|5%|2 Years": [
    { premium: 50000, db: 0.999992, ltc: 0.999993 },
    { premium: 75000, db: 0.999997, ltc: 0.999998 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1.000002, ltc: 1.000001 },
    { premium: 150000, db: 1.000003, ltc: 1 },
  ],
  "5%|5%|4 Years": [
    { premium: 50000, db: 1, ltc: 1.000003 },
    { premium: 75000, db: 1, ltc: 1.000001 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1.000007, ltc: 1.000008 },
    { premium: 150000, db: 1.000006, ltc: 1.000006 },
    { premium: 200000, db: 1.000004, ltc: 1.000004 },
  ],
  "5%|5%|6 Years": [
    { premium: 75000, db: 1.000004, ltc: 1.000003 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1.000007, ltc: 1.000007 },
    { premium: 150000, db: 1.000004, ltc: 1.000005 },
    { premium: 200000, db: 1.000005, ltc: 1.000006 },
  ],
  "5%|5%|Lifetime": [
    { premium: 75000, db: 0.999996, ltc: 1 },
    { premium: 100000, db: 1, ltc: 1 },
    { premium: 125000, db: 1.000002, ltc: 1 },
    { premium: 150000, db: 0.999996, ltc: 1 },
    { premium: 200000, db: 1, ltc: 1 },
  ],
};

const OA_CAT3_10PAY_SINGLE_LIFE_BANDS: ConfigBands = {
  "None|None|2 Years": [
    { premium: 5000, db: 0.979951, ltc: 0.979951 },
    { premium: 7500, db: 0.999998, ltc: 0.999998 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000002, ltc: 1.000002 },
    { premium: 15500, db: 1.000003, ltc: 1.000003 },
    { premium: 20000, db: 1.000004, ltc: 1.000004 },
    { premium: 25000, db: 1.000004, ltc: 1.000004 },
    { premium: 30000, db: 1.000005, ltc: 1.000005 },
  ],
  "None|None|4 Years": [
    { premium: 5000, db: 0.979963, ltc: 0.979963 },
    { premium: 7500, db: 0.986602, ltc: 0.986602 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1, ltc: 1 },
    { premium: 15500, db: 1.000001, ltc: 1.000001 },
    { premium: 20000, db: 1.000004, ltc: 1.000004 },
    { premium: 25000, db: 1.000003, ltc: 1.000003 },
    { premium: 30000, db: 1.000003, ltc: 1.000003 },
  ],
  "None|None|6 Years": [
    { premium: 5000, db: 0.979944, ltc: 0.979944 },
    { premium: 7500, db: 0.986615, ltc: 0.986615 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000003, ltc: 1.000003 },
    { premium: 15500, db: 1, ltc: 1 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 1.000002, ltc: 1.000002 },
    { premium: 30000, db: 1.000003, ltc: 1.000003 },
  ],
  "None|None|Lifetime": [
    { premium: 5000, db: 0.979928, ltc: 1 },
    { premium: 7500, db: 0.98661, ltc: 1 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 0.999997, ltc: 1 },
    { premium: 15500, db: 0.999998, ltc: 1 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 1.000002, ltc: 1 },
    { premium: 30000, db: 1, ltc: 1 },
  ],
  "None|3%|2 Years": [
    { premium: 5000, db: 0.979969, ltc: 0.979965 },
    { premium: 7500, db: 0.986602, ltc: 0.986601 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000003, ltc: 1.000002 },
    { premium: 15500, db: 1, ltc: 1.000001 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 1.000002, ltc: 1.000001 },
    { premium: 30000, db: 1.017653, ltc: 0.938957 },
  ],
  "None|3%|4 Years": [
    { premium: 5000, db: 0.979965, ltc: 0.979963 },
    { premium: 7500, db: 0.986676, ltc: 0.986674 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000005, ltc: 1.000004 },
    { premium: 15500, db: 1.000004, ltc: 1.000003 },
    { premium: 20000, db: 1.000008, ltc: 1.000008 },
    { premium: 25000, db: 1.000006, ltc: 1.000006 },
    { premium: 30000, db: 1.01742, ltc: 0.93928 },
  ],
  "None|3%|6 Years": [
    { premium: 5000, db: 0.979999, ltc: 0.98 },
    { premium: 7500, db: 0.986675, ltc: 0.986675 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000003, ltc: 1.000003 },
    { premium: 15500, db: 1.000001, ltc: 1.000003 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 1.000002, ltc: 1.000002 },
    { premium: 30000, db: 1.000003, ltc: 1.000003 },
  ],
  "None|3%|Lifetime": [
    { premium: 5000, db: 0.980009, ltc: 1 },
    { premium: 7500, db: 0.986651, ltc: 1 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000003, ltc: 1 },
    { premium: 15500, db: 1.000003, ltc: 1 },
    { premium: 20000, db: 1.000005, ltc: 1 },
    { premium: 25000, db: 1.000006, ltc: 1 },
    { premium: 30000, db: 1.000006, ltc: 1 },
  ],
  "None|5%|2 Years": [
    { premium: 5000, db: 0.979983, ltc: 0.979988 },
    { premium: 7500, db: 0.986691, ltc: 0.986693 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 0.999997, ltc: 0.999999 },
    { premium: 15500, db: 0.999996, ltc: 0.999998 },
    { premium: 20000, db: 1.005535, ltc: 0.986536 },
    { premium: 25000, db: 1.040992, ltc: 0.900288 },
    { premium: 30000, db: 1.064629, ltc: 0.842789 },
  ],
  "None|5%|4 Years": [
    { premium: 5000, db: 0.980027, ltc: 0.980025 },
    { premium: 7500, db: 0.986634, ltc: 0.986634 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 0.999994, ltc: 0.999994 },
    { premium: 15500, db: 0.999995, ltc: 0.999995 },
    { premium: 20000, db: 0.999996, ltc: 0.999996 },
    { premium: 25000, db: 1.044892, ltc: 0.88326 },
    { premium: 30000, db: 1.077906, ltc: 0.797413 },
  ],
  "None|5%|6 Years": [
    { premium: 5000, db: 0.990601, ltc: 0.9906 },
    { premium: 7500, db: 0.986653, ltc: 0.986652 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 0.999997, ltc: 0.999996 },
    { premium: 15500, db: 1, ltc: 1 },
    { premium: 20000, db: 1, ltc: 0.999999 },
    { premium: 25000, db: 1.022132, ltc: 0.965806 },
    { premium: 30000, db: 1.093385, ltc: 0.855708 },
  ],
  "None|5%|Lifetime": [
    { premium: 5000, db: 1.020392, ltc: 1 },
    { premium: 7500, db: 0.996576, ltc: 1 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010102, ltc: 1 },
    { premium: 15500, db: 1.010103, ltc: 1 },
    { premium: 20000, db: 1.010104, ltc: 1 },
    { premium: 25000, db: 1.024836, ltc: 1 },
    { premium: 30000, db: 1.102885, ltc: 1 },
  ],
  "3%|3%|2 Years": [
    { premium: 5000, db: 0.980012, ltc: 0.980009 },
    { premium: 7500, db: 0.986669, ltc: 0.98667 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1, ltc: 1 },
    { premium: 15500, db: 1.000004, ltc: 1.000005 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 1.000004, ltc: 1.000005 },
    { premium: 30000, db: 1.000003, ltc: 1.000004 },
  ],
  "3%|3%|4 Years": [
    { premium: 5000, db: 0.980016, ltc: 0.980012 },
    { premium: 7500, db: 0.986618, ltc: 0.986617 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 0.999997, ltc: 0.999996 },
    { premium: 15500, db: 0.999998, ltc: 0.999998 },
    { premium: 20000, db: 1, ltc: 0.999999 },
    { premium: 25000, db: 0.999998, ltc: 0.999998 },
    { premium: 30000, db: 0.999997, ltc: 0.999996 },
  ],
  "3%|3%|6 Years": [
    { premium: 5000, db: 1.051697, ltc: 1.051695 },
    { premium: 7500, db: 0.99659, ltc: 0.996589 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.01009, ltc: 1.01009 },
    { premium: 15500, db: 1.010086, ltc: 1.010087 },
    { premium: 20000, db: 1.010088, ltc: 1.010088 },
    { premium: 25000, db: 1.010087, ltc: 1.010087 },
    { premium: 30000, db: 1.01009, ltc: 1.01009 },
  ],
  "3%|3%|Lifetime": [
    { premium: 5000, db: 1.071694, ltc: 1 },
    { premium: 7500, db: 0.996639, ltc: 1 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010083, ltc: 1 },
    { premium: 15500, db: 1.010087, ltc: 1 },
    { premium: 20000, db: 1.010088, ltc: 1 },
  ],
  "5%|5%|2 Years": [
    { premium: 7500, db: 0.996562, ltc: 0.996561 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010077, ltc: 1.010078 },
    { premium: 15500, db: 1.010081, ltc: 1.010084 },
    { premium: 20000, db: 1.010082, ltc: 1.010083 },
    { premium: 25000, db: 1.010081, ltc: 1.010082 },
  ],
  "5%|5%|4 Years": [
    { premium: 5000, db: 1.114993, ltc: 1.114995 },
    { premium: 7500, db: 0.996693, ltc: 0.996693 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010151, ltc: 1.010151 },
    { premium: 15500, db: 1.010151, ltc: 1.010151 },
    { premium: 20000, db: 1.010149, ltc: 1.010149 },
    { premium: 25000, db: 1.010148, ltc: 1.010148 },
  ],
  "5%|5%|6 Years": [
    { premium: 5000, db: 1.257585, ltc: 1.257583 },
    { premium: 7500, db: 0.996677, ltc: 0.996677 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.01013, ltc: 1.01013 },
    { premium: 15500, db: 1.010121, ltc: 1.01012 },
    { premium: 20000, db: 1.010128, ltc: 1.010127 },
    { premium: 25000, db: 1.010126, ltc: 1.010126 },
  ],
  "5%|5%|Lifetime": [
    { premium: 5000, db: 1.277188, ltc: 1 },
    { premium: 7500, db: 0.996646, ltc: 1 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010131, ltc: 1 },
    { premium: 15500, db: 1.01013, ltc: 1 },
    { premium: 20000, db: 1.010127, ltc: 1 },
    { premium: 25000, db: 1.010129, ltc: 1 },
    { premium: 30000, db: 1.010131, ltc: 1 },
  ],
};

const OA_CAT3_10PAY_JOINT_LIFE_BANDS: ConfigBands = {
  "None|None|2 Years": [
    { premium: 5000, db: 0.980015, ltc: 0.980015 },
    { premium: 7500, db: 1.000004, ltc: 1.000004 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000004, ltc: 1.000004 },
    { premium: 15500, db: 1.000006, ltc: 1.000006 },
    { premium: 20000, db: 1.000006, ltc: 1.000006 },
    { premium: 25000, db: 1.000005, ltc: 1.000005 },
    { premium: 30000, db: 1.000004, ltc: 1.000004 },
  ],
  "None|None|4 Years": [
    { premium: 5000, db: 0.979891, ltc: 0.979891 },
    { premium: 7500, db: 0.999998, ltc: 0.999998 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000002, ltc: 1.000002 },
    { premium: 15500, db: 1.000001, ltc: 1.000001 },
    { premium: 20000, db: 1.000003, ltc: 1.000003 },
    { premium: 25000, db: 1.000004, ltc: 1.000004 },
    { premium: 30000, db: 1.000004, ltc: 1.000004 },
  ],
  "None|None|6 Years": [
    { premium: 5000, db: 0.979974, ltc: 0.979974 },
    { premium: 7500, db: 1.000005, ltc: 1.000005 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000005, ltc: 1.000005 },
    { premium: 15500, db: 1.000003, ltc: 1.000003 },
    { premium: 20000, db: 1.000004, ltc: 1.000004 },
    { premium: 25000, db: 1.000003, ltc: 1.000003 },
    { premium: 30000, db: 1.000005, ltc: 1.000005 },
  ],
  "None|None|Lifetime": [
    { premium: 5000, db: 0.979924, ltc: 1 },
    { premium: 7500, db: 0.986693, ltc: 1 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 0.999997, ltc: 1 },
    { premium: 15500, db: 0.999999, ltc: 1 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 0.999998, ltc: 1 },
    { premium: 30000, db: 1, ltc: 1 },
  ],
  "None|3%|2 Years": [
    { premium: 5000, db: 0.979933, ltc: 0.979931 },
    { premium: 7500, db: 0.999998, ltc: 0.999998 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 0.999998, ltc: 0.999998 },
    { premium: 15500, db: 0.999999, ltc: 0.999999 },
    { premium: 20000, db: 1, ltc: 1 },
    { premium: 25000, db: 1.044973, ltc: 0.959114 },
    { premium: 30000, db: 1.099619, ltc: 0.909435 },
  ],
  "None|3%|4 Years": [
    { premium: 5000, db: 0.980034, ltc: 0.980035 },
    { premium: 7500, db: 1, ltc: 1 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000005, ltc: 1.000005 },
    { premium: 15500, db: 1.000002, ltc: 1.000002 },
    { premium: 20000, db: 1.000004, ltc: 1.000003 },
    { premium: 25000, db: 1.005748, ltc: 0.994517 },
    { premium: 30000, db: 1.095574, ltc: 0.908737 },
  ],
  "None|3%|6 Years": [
    { premium: 5000, db: 0.980006, ltc: 0.980006 },
    { premium: 7500, db: 0.986664, ltc: 0.986663 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000006, ltc: 1.000006 },
    { premium: 15500, db: 1.000003, ltc: 1.000003 },
    { premium: 20000, db: 1.000005, ltc: 1.000004 },
    { premium: 25000, db: 1.000004, ltc: 1.000004 },
    { premium: 30000, db: 1.000006, ltc: 1.000006 },
  ],
  "None|3%|Lifetime": [
    { premium: 5200, db: 0.990639, ltc: 1 },
    { premium: 7500, db: 0.996605, ltc: 1 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010143, ltc: 1 },
    { premium: 15500, db: 1.010144, ltc: 1 },
    { premium: 20000, db: 1.010143, ltc: 1 },
    { premium: 25000, db: 1.010143, ltc: 1 },
    { premium: 30000, db: 1.010143, ltc: 1 },
  ],
  "None|5%|2 Years": [
    { premium: 5000, db: 0.97991, ltc: 0.979914 },
    { premium: 7500, db: 1.000005, ltc: 1.000007 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000005, ltc: 1.000005 },
    { premium: 15500, db: 1.000003, ltc: 1.000003 },
    { premium: 20000, db: 1.065321, ltc: 0.965902 },
    { premium: 25000, db: 1.159267, ltc: 0.916854 },
    { premium: 30000, db: 1.221898, ltc: 0.884155 },
  ],
  "None|5%|4 Years": [
    { premium: 5000, db: 0.979943, ltc: 0.979941 },
    { premium: 7500, db: 0.986688, ltc: 0.986688 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 0.999997, ltc: 0.999997 },
    { premium: 15500, db: 0.999999, ltc: 0.999998 },
    { premium: 20000, db: 1, ltc: 0.999999 },
    { premium: 25000, db: 1.14587, ltc: 0.910468 },
    { premium: 30000, db: 1.250459, ltc: 0.846274 },
  ],
  "None|5%|6 Years": [
    { premium: 5800, db: 0.992629, ltc: 0.992629 },
    { premium: 7500, db: 0.996553, ltc: 0.996551 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010085, ltc: 1.010084 },
    { premium: 15500, db: 1.010086, ltc: 1.010085 },
    { premium: 20000, db: 1.010086, ltc: 1.010086 },
    { premium: 25000, db: 1.010088, ltc: 1.010087 },
    { premium: 30000, db: 1.151815, ltc: 0.957059 },
  ],
  "None|5%|Lifetime": [
    { premium: 6300, db: 0.994045, ltc: 1 },
    { premium: 7500, db: 0.996622, ltc: 1 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010143, ltc: 1 },
    { premium: 15500, db: 1.010138, ltc: 1 },
    { premium: 20000, db: 1.010141, ltc: 1 },
    { premium: 25000, db: 1.010139, ltc: 1 },
    { premium: 30000, db: 1.043702, ltc: 1 },
  ],
  "3%|3%|2 Years": [
    { premium: 5000, db: 0.97991, ltc: 0.97991 },
    { premium: 7500, db: 0.98664, ltc: 0.986643 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.000006, ltc: 1.000007 },
    { premium: 15500, db: 1.000005, ltc: 1.000006 },
    { premium: 20000, db: 1.000004, ltc: 1.000004 },
    { premium: 25000, db: 1.000007, ltc: 1.000007 },
  ],
  "3%|3%|4 Years": [
    { premium: 5000, db: 0.97999, ltc: 0.979992 },
    { premium: 7500, db: 0.986642, ltc: 0.986641 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1, ltc: 1.000001 },
    { premium: 15500, db: 1.000004, ltc: 1.000004 },
    { premium: 20000, db: 1.000005, ltc: 1.000006 },
    { premium: 25000, db: 1.000004, ltc: 1.000005 },
    { premium: 30000, db: 1.000003, ltc: 1.000004 },
  ],
  "3%|3%|6 Years": [
    { premium: 5700, db: 0.992399, ltc: 0.992401 },
    { premium: 7500, db: 0.996717, ltc: 0.996719 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010163, ltc: 1.010163 },
    { premium: 15500, db: 1.010166, ltc: 1.010168 },
    { premium: 20000, db: 1.010163, ltc: 1.010164 },
    { premium: 25000, db: 1.010163, ltc: 1.010164 },
    { premium: 30000, db: 1.010167, ltc: 1.010168 },
  ],
  "3%|3%|Lifetime": [
    { premium: 6100, db: 0.993504, ltc: 1 },
    { premium: 7500, db: 0.996608, ltc: 1 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.01011, ltc: 1 },
    { premium: 15500, db: 1.010114, ltc: 1 },
    { premium: 20000, db: 1.010114, ltc: 1 },
    { premium: 25000, db: 1.010112, ltc: 1 },
    { premium: 30000, db: 1.010114, ltc: 1 },
  ],
  "5%|5%|2 Years": [
    { premium: 5600, db: 0.99209, ltc: 0.992092 },
    { premium: 7500, db: 0.996659, ltc: 0.99666 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010149, ltc: 1.010146 },
    { premium: 15500, db: 1.010146, ltc: 1.010145 },
    { premium: 20000, db: 1.01015, ltc: 1.010148 },
    { premium: 25000, db: 1.010152, ltc: 1.010151 },
  ],
  "5%|5%|4 Years": [
    { premium: 6200, db: 0.99381, ltc: 0.993811 },
    { premium: 7500, db: 0.99665, ltc: 0.996651 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010108, ltc: 1.010111 },
    { premium: 15500, db: 1.010105, ltc: 1.010105 },
    { premium: 20000, db: 1.010106, ltc: 1.010107 },
    { premium: 25000, db: 1.01011, ltc: 1.010111 },
  ],
  "5%|5%|6 Years": [
    { premium: 7600, db: 0.996817, ltc: 0.996817 },
    { premium: 7600, db: 0.996817, ltc: 0.996817 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.010128, ltc: 1.010127 },
    { premium: 15500, db: 1.010131, ltc: 1.010131 },
    { premium: 20000, db: 1.010131, ltc: 1.010131 },
    { premium: 25000, db: 1.010132, ltc: 1.010131 },
    { premium: 30000, db: 1.010128, ltc: 1.010128 },
  ],
  "5%|5%|Lifetime": [
    { premium: 8100, db: 0.997642, ltc: 1 },
    { premium: 8100, db: 0.997642, ltc: 1 },
    { premium: 10000, db: 1, ltc: 1 },
    { premium: 15000, db: 1.003327, ltc: 1 },
    { premium: 15500, db: 1.003578, ltc: 1 },
    { premium: 20000, db: 1.0101, ltc: 1 },
    { premium: 25000, db: 1.010102, ltc: 1 },
    { premium: 30000, db: 1.010103, ltc: 1 },
  ],
};

const OA_CAT3_PRODUCT_BANDS: Record<string, ConfigBands> = {
  sp_joint: OA_CAT3_SINGLE_PAY_JOINT_LIFE_BANDS,
  "10pay_single": OA_CAT3_10PAY_SINGLE_LIFE_BANDS,
  "10pay_joint": OA_CAT3_10PAY_JOINT_LIFE_BANDS,
};

export const OA_BANDS_BY_CATEGORY: Record<number, Record<string, ConfigBands>> = {
  1: OA_CAT1_PRODUCT_BANDS,
  3: OA_CAT3_PRODUCT_BANDS,
};
